import {
  ChangeEvent,
  Children,
  forwardRef,
  LegacyRef,
  ReactNode,
  useMemo,
} from 'react';
import classNames from 'classnames';

import { ErrorMessage } from '@betrybe/ada-react';
import IconArrowDown from '@/components/tokens';

export type OptionsProps = {
  value: string;
  label: string;
};

type SelectProps = {
  value: string;
  children?: ReactNode;
  disabled?: boolean;
  errorMessage: string | undefined;
  options: OptionsProps[];
  placeholder: string;
  id: string;
  name: string;
  onChange: (event: ChangeEvent) => void;
  className?: string;
};

const Select = forwardRef(function Select(
  props: SelectProps,
  ref: LegacyRef<HTMLSelectElement>,
) {
  const {
    value,
    children,
    disabled,
    errorMessage,
    options,
    id,
    placeholder,
    className: customClassName,
    ...selectProps
  } = props;

  const className = useMemo(
    () =>
      classNames('select', customClassName, {
        filled: !!value,
        enabled: !value && !disabled,
        disabled: !!disabled,
        error: !!errorMessage,
      }),
    [value, disabled, customClassName, errorMessage],
  );

  const placeholderOption = () => {
    if (!placeholder) {
      return null;
    }

    return (
      <option value="" className="hidden">
        {placeholder}
      </option>
    );
  };

  const optionsFromSelectOptions = () => {
    if (!options) {
      return null;
    }
    if (!Array.isArray(options)) {
      throw new Error(`Unhandled options type: ${typeof options}`);
    }

    return options.map((item) => (
      <option key={item.value} value={item.value}>
        {item.label}
      </option>
    ));
  };

  return (
    <>
      <div className="relative select-wrapper">
        <select
          {...selectProps}
          ref={ref}
          value={value}
          disabled={disabled}
          className={className}
          id={id}
        >
          {placeholderOption()}
          {optionsFromSelectOptions()}
          {Children.toArray(children)}
        </select>
        <div className="select-icon">
          <IconArrowDown width={12} height={7} />
        </div>
      </div>
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </>
  );
});

Select.defaultProps = {
  className: '',
  children: '',
  disabled: false,
};

export default Select;
